import React, { FC } from 'react'
import { Link } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { SidebarMenuItemWithSub } from './SidebarMenuItemWithSub'
import { SidebarMenuItem } from './SidebarMenuItem'
import { brandSettings } from '../../../../../app/modules/settings/BrandSettings'
import { TxnType } from '../../../../../app/types'
import { useAppContext } from '../../../../../app/modules/apps/AppContext'
import { canAccess, canAccessAny } from '../../../../../app/modules/auth/accessControl'
import { getEffectiveEdition, canUseModule, Edition } from '../../../../../app/modules/subscription/subscriptionAccess'
import { KTIcon } from '../../../../helpers'

/** Sidebar item that shows a lock icon and opens the upgrade modal on click. */
const LockedSidebarMenuItem: FC<{ title: string; icon?: string; requiredEdition: Edition }> = ({
  title, icon, requiredEdition,
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    window.dispatchEvent(new CustomEvent('subscription:upgrade-required', {
      detail: { message: `${title} is available on ${requiredEdition} or higher. Upgrade your plan to unlock it.` },
    }))
  }
  return (
    <div className='menu-item'>
      <a className='menu-link without-sub' href='#' onClick={handleClick}>
        {icon && (
          <span className='menu-icon'>
            <KTIcon iconName={icon} className='fs-2 text-muted' />
          </span>
        )}
        <span className='menu-title text-muted d-flex align-items-center gap-2'>
          {title}
          <span
            title={`Available on ${requiredEdition} or higher`}
            style={{ cursor: 'default', lineHeight: 1 }}
          >
            <i className='bi bi-lock-fill text-warning' style={{ fontSize: '11px' }} />
          </span>
        </span>
      </a>
    </div>
  )
}

const SidebarMenuMain = () => {
  const intl = useIntl()
  const { bizSettings, roleAccess, subscriptionData } = useAppContext()
  const isAdmin = roleAccess?.isAdmin ?? false
  const permissions = roleAccess?.permissions ?? []
  const can = (objectTypes: string[]) => isAdmin || canAccessAny(permissions, objectTypes, 'read')
  const canWrite = (objectType: string) => isAdmin || canAccess(permissions, objectType, 'write')
  const effectiveEdition = subscriptionData
    ? getEffectiveEdition(
        subscriptionData.currentPlan.edition,
        subscriptionData.currentPlan.status,
        subscriptionData.currentPlan.isTrial
      )
    : 'Free'
  return (
    <>
      <SidebarMenuItem
        to='/dashboard'
        icon='home-1'
        title={intl.formatMessage({ id: 'MENU.DASHBOARD' })}
        fontIcon='bi-app-indicator'
      />
      {can(['Contact']) && (
        <SidebarMenuItemWithSub
          to='/crafted/pages'
          title={'Contacts'.toTranslatedDisplay()}
          fontIcon='bi-archive'
          icon='people'
        >
          {canWrite('Contact') && <SidebarMenuItem to='/contacts/create-contact' title={'Create Contact'.toTranslatedDisplay()} icon='user' />}
          <SidebarMenuItem to='/contacts/view-contacts' title={'View Contacts'.toTranslatedDisplay()} icon='profile-user' />
        </SidebarMenuItemWithSub>
      )}

      {can(['Item', 'PriceList']) && (
        <SidebarMenuItemWithSub
          to='/crafted/pages'
          title={'Items'.toTranslatedDisplay()}
          fontIcon='bi-archive'
          icon='lots-shopping'
        >
          {canWrite('Item') && <SidebarMenuItem to='/items/create-item' title={'Create Item'.toTranslatedDisplay()} icon='add-item' />}
          {can(['Item']) && <SidebarMenuItem to='/items/view-items' title={'View Items'.toTranslatedDisplay()} icon='note' />}
          {can(['PriceList']) && <SidebarMenuItem to='/items/view-price-lists' title={'Price List'.toTranslatedDisplay()} icon='note' />}
        </SidebarMenuItemWithSub>
      )}
      {can(['BankAccount', 'BankTransaction']) && (
        canUseModule('Banking', effectiveEdition)
          ? <SidebarMenuItem to='/banking/view-bank-accounts' title={'Banking'.toTranslatedDisplay()} icon='bank' />
          : <LockedSidebarMenuItem title={'Banking'.toTranslatedDisplay()} icon='bank' requiredEdition='Standard' />
      )}
      {can(['Documents']) && <SidebarMenuItem to='/documents' title={'Documents'.toTranslatedDisplay()} icon='folder' />}
      {can(['Invoice']) && (
        canUseModule('Projects', effectiveEdition)
          ? (
            <SidebarMenuItemWithSub
              to='/projects'
              title={'Projects'.toTranslatedDisplay()}
              fontIcon='bi-kanban'
              icon='abstract-26'
            >
              {canWrite('Invoice') && <SidebarMenuItem to='/projects/new' title={'New Project'.toTranslatedDisplay()} icon='plus' />}
              <SidebarMenuItem to='/projects/list' title={'Project List'.toTranslatedDisplay()} icon='tablet-book' />
            </SidebarMenuItemWithSub>
          )
          : <LockedSidebarMenuItem title={'Projects'.toTranslatedDisplay()} icon='abstract-26' requiredEdition='Premium' />
      )}
      <div className='menu-item'>
        <div className='menu-content pt-8 pb-2'>
          <span className='menu-section text-muted text-uppercase fs-8 ls-1'>{'Transactions'.toTranslatedDisplay()}</span>
        </div>
      </div>

      {can(['Invoice', 'Estimate', 'SalesOrder', 'ProformaInvoice', 'SalesReturn', 'CustomerPayment', 'Delivery']) && (
        <SidebarMenuItemWithSub
          to='/crafted/pages'
          title={'Sales'.toTranslatedDisplay()}
          fontIcon='bi-archive'
          icon='office-bag'
        >
          {can(['Invoice']) && <SidebarMenuItem to='/transactions/view-invoices' title={'Invoices'.toTranslatedDisplay()} icon='document' />}
          {can(['Estimate']) && <SidebarMenuItem to='/transactions/view-estimates' title={'Estimates'.toTranslatedDisplay()} icon='tablet-book' />}
          {can(['ProformaInvoice']) && !brandSettings.hideTxnTypes.includes(TxnType.ProformaInvoice) &&
            <SidebarMenuItem to='/transactions/view-proforma-invoices' title={'Proforma Invoice'.toTranslatedDisplay()} icon='tablet-book' />
          }
          {can(['CustomerPayment']) && <SidebarMenuItem to='/transactions/view-payments/CustomerPayment' title={'Customer Payments'.toTranslatedDisplay()} icon='wallet' />}
          {can(['SalesReturn']) && <SidebarMenuItem to='/transactions/view-sales-returns' title={'Sales Returns'.toTranslatedDisplay()} icon='update-file' />}
          {can(['SalesOrder']) && !brandSettings.hideTxnTypes.includes(TxnType.SalesOrder) &&
            <SidebarMenuItem to='/transactions/view-sales-orders' title={'Sales Orders'.toTranslatedDisplay()} icon='tablet-text-up' />
          }
          {can(['Delivery']) && !brandSettings.hideTxnTypes.includes(TxnType.Delivery) &&
            <SidebarMenuItem to='/transactions/view-deliveries' title={'Delivery'.toTranslatedDisplay()} icon='truck' />
          }
        </SidebarMenuItemWithSub>
      )}

      {can(['Expense', 'Bills', 'PurchaseOrder', 'VendorPayment', 'PurchaseReturn']) && (
        <SidebarMenuItemWithSub
          to='/transactions/pages'
          title={'Expense/Purchases'.toTranslatedDisplay()}
          fontIcon='bi-archive'
          icon='purchase'
        >
          {can(['Expense']) && <SidebarMenuItem to='/transactions/view-expenses' title={'Expenses'.toTranslatedDisplay()} icon='two-credit-cart' />}
          {can(['Bills']) && <SidebarMenuItem to='/transactions/view-purchase-bills' title={'Purchase Bills'.toTranslatedDisplay()} icon='document' />}
          {can(['PurchaseOrder']) && <SidebarMenuItem to='/transactions/view-purchase-orders' title={'Purchase Orders'.toTranslatedDisplay()} icon='tablet-book' />}
          {can(['VendorPayment']) && <SidebarMenuItem to='/transactions/view-payments/VendorPayment' title={'Vendor Payments'.toTranslatedDisplay()} icon='wallet' />}
          {can(['PurchaseReturn']) && <SidebarMenuItem to='/transactions/view-purchase-returns' title={'Purchase Returns'.toTranslatedDisplay()} icon='update-file' />}
        </SidebarMenuItemWithSub>
      )}

      {can(['StockAdjustment', 'JournalEntry', 'BusinessTransaction']) && (
        <SidebarMenuItemWithSub
          to='/transactions/pages'
          title={'Other Transactions'.toTranslatedDisplay()}
          fontIcon='bi-archive'
          icon='questionnaire-tablet'
        >
          {can(['StockAdjustment']) && !brandSettings.hideTxnTypes.includes(TxnType.StockAdjustment) &&
            <SidebarMenuItem to='/transactions/view-stock-adjustments' title={'Stock Adjustments'.toTranslatedDisplay()} icon='delivery-door' />
          }
          {can(['JournalEntry']) && <SidebarMenuItem to='/transactions/view-journals' title={'Journals'.toTranslatedDisplay()} icon='bill' />}
          {can(['BusinessTransaction']) && <SidebarMenuItem to='/transactions/view-business-transactions' title={'Business Transactions'.toTranslatedDisplay()} icon='tablet-book' />}
          {isAdmin && <SidebarMenuItem to='/transactions/tax-summary' title={'Tax Summary/Payments'.toTranslatedDisplay()} icon='finance-calculator' />}
          {canUseModule('Manufacturing', effectiveEdition) && (
            <>
              <SidebarMenuItem to='/manufacturing/work-orders' title={'Work Orders'.toTranslatedDisplay()} icon='tablet-book' />
              <SidebarMenuItem to='/manufacturing/boms' title={'Bills of Materials'.toTranslatedDisplay()} icon='note-2' />
            </>
          )}
          {bizSettings?.budgetSettings?.enabled && canUseModule('Budgets', effectiveEdition) && <SidebarMenuItem to='/budgets' title={'Budgets'.toTranslatedDisplay()} icon='calculator' />}
        </SidebarMenuItemWithSub>
      )}

      <div className='menu-item'>
        <div className='menu-content pt-8 pb-2'>
          <span className='menu-section text-muted text-uppercase fs-8 ls-1'>{'Reports'.toTranslatedDisplay()}</span>
        </div>
      </div>

      {can(['FinancialReport', 'LedgerReport', 'SalesReport', 'PurchaseReport', 'InventoryReport', 'Expense']) && (
        <SidebarMenuItemWithSub
          to='/reports/report-menu'
          title={'Reports'.toTranslatedDisplay()}
          fontIcon='bi-archive'
          icon='chart-line'
        >
          <SidebarMenuItem to='/reports/report-menu' title={'All Reports'.toTranslatedDisplay()} icon='graph' />
          {can(['FinancialReport']) && <SidebarMenuItem to='/reports/financial-reports' title={'Financial Reports'.toTranslatedDisplay()} icon='graph-up' />}
          {can(['FinancialReport']) && <SidebarMenuItem to='/reports/management-reports' title={'Management Reports'.toTranslatedDisplay()} icon='graph-2' />}
          {can(['SalesReport']) && <SidebarMenuItem to='/reports/sales-reports' title={'Sales Reports'.toTranslatedDisplay()} icon='chart-simple-3' />}
          {can(['PurchaseReport']) && <SidebarMenuItem to='/reports/purchase-reports' title={'Purchase Reports'.toTranslatedDisplay()} icon='chart-simple-2' />}
          {can(['Expense']) && <SidebarMenuItem to='/reports/expense-reports' title={'Expense Reports'.toTranslatedDisplay()} icon='wallet' />}
          {can(['InventoryReport']) && brandSettings.inventorySetting !== false &&
            <SidebarMenuItem to='/reports/stock-reports' title={'Stock Reports'.toTranslatedDisplay()} icon='chart-line-up-2' />
          }
          {can(['SalesReport']) && bizSettings?.taxFlag !== false &&
            <SidebarMenuItem to='/reports/tax-reports' title={'Tax Reports'.toTranslatedDisplay()} icon='graph-up' />
          }
          <SidebarMenuItem to='/reports/audit-report' title={'Audit Report'.toTranslatedDisplay()} icon='shield-tick' />
        </SidebarMenuItemWithSub>
      )}


      {can(['Settings']) && <SidebarMenuItem to='/settings' title={'Settings'.toTranslatedDisplay()} icon='setting-2' />}
      {can(['RecycleBin']) && (canUseModule('BackupRestore', effectiveEdition)
        ? <SidebarMenuItem to='/settings/recycle-bin' title={'Recycle Bin'.toTranslatedDisplay()} icon='trash' />
        : <LockedSidebarMenuItem title={'Recycle Bin'.toTranslatedDisplay()} icon='trash' requiredEdition='Standard' />
      )}
    </>
  )
}

export { SidebarMenuMain }
